<script setup lang="ts">
    import type { AcademyFilter, AcademyFilterOption, AcademyFilterStateValue } from '~/types/academy/filters';

    const props = defineProps<{
        filter: AcademyFilter;
        state?: AcademyFilterStateValue;
    }>();

    const emit = defineEmits<{
        'toggle-option': [optionKey: string];
    }>();

    const CAPITALIZED_FILTER_CODES = ['product'];

    const { t, te } = useI18n();

    const LABEL_TRANSLATION_MAPPING = {
        'in-person': 'academy.badgeLabel.inPerson',
        'live-webinar': 'academy.badgeLabel.liveWebinar',
        cme: 'academy.badgeLabel.cme'
    } as const;

    /**
     * Docebo values are translated in Docebo and shown as they are. Values that exist only on
     * Storyblok events can be labelled via `academy.trainings.filter.option.<filter code>.<value>`.
     */
    function translateOptionLabel(filterCode: string, option: AcademyFilterOption): string {
        const optionKey = `academy.trainings.filter.option.${filterCode}.${option.label}`;
        if (option.storyblokOnly && te(optionKey)) {
            return t(optionKey);
        }

        const mappedKey = LABEL_TRANSLATION_MAPPING[option.label as keyof typeof LABEL_TRANSLATION_MAPPING];
        if (mappedKey && te(mappedKey)) {
            return t(mappedKey);
        }

        return option.label;
    }

    function formatOptionLabel(option: AcademyFilterOption): string {
        const label = translateOptionLabel(props.filter.code, option);

        if (option.count === undefined) {
            return label;
        }

        // TODO: replace `0` with actual count `option.count` once implemented properly in the backend
        return t('academy.trainings.filterOptionCount', { label, count: 0 });
    }

    function isOptionSelected(optionKey: string): boolean {
        if (!props.state || props.state.type !== 'checkbox_filter') {
            return false;
        }

        return props.state.selectedKeys.includes(optionKey);
    }
</script>

<template>
    <div class="filter-item">
        <template v-if="filter.type === 'checkbox_filter' && Array.isArray(filter.params.options)">
            <template
                v-for="option in filter.params.options"
                :key="`${filter.code}-${option.key}`"
            >
                <BasicCheckboxLabel
                    :id="`${filter.code}-${option.key}-checkbox`"
                    :label-class="{ capitalize: CAPITALIZED_FILTER_CODES.includes(filter.code) }"
                    :label="formatOptionLabel(option)"
                    :model-value="isOptionSelected(option.key)"
                    variant="primary"
                    @update:model-value="emit('toggle-option', option.key)"
                />
            </template>
        </template>
    </div>
</template>

<style scoped>
    .filter-item {
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        padding: var(--spacing);

        :deep(.capitalize) {
            text-transform: capitalize;
        }
    }
</style>
